import type { Insight } from './types';

/** Whole-number percentage of `count` out of `total`, clamped to [0, 100]. */
export const percentOf = (count: number, total: number): number => {
  if (total <= 0) return 0;
  const ratio = Math.min(Math.max(count / total, 0), 1);
  return Math.round(ratio * 100);
};

/** Sum of the numeric `count` field across a list of records; negative counts are treated as 0. */
export const sumCounts = <T extends { count: number }>(items: T[]): number =>
  items.reduce((total, item) => total + Math.max(item.count, 0), 0);

/** Largest `count` across a list of records; 0 when empty or all non-positive. */
export const maxCount = <T extends { count: number }>(items: T[]): number =>
  items.reduce((largest, item) => Math.max(largest, item.count, 0), 0);

/**
 * Whole-number percentages for `counts` out of `total`, reconciled to sum to
 * exactly 100 (when total > 0 and `counts` fully accounts for it) via the
 * largest-remainder method. Rounding each option independently (plain
 * `percentOf` per item) can drift a card's legend away from 100% — e.g.
 * 12/11/10/7/6/6/4 out of 56 rounds to 21/20/18/13/11/11/7, which sums to 101.
 *
 * `total` is trusted as the authoritative denominator and isn't required to
 * equal `sum(counts)` — if the backend's option counts don't fully add up to
 * it, the result honestly falls short of (or exceeds) 100 rather than forcing
 * an appearance of consistency the data doesn't have.
 */
export const percentagesSummingTo100 = (counts: number[], total: number): number[] => {
  if (total <= 0) return counts.map(() => 0);

  const clamped = counts.map(count => Math.max(count, 0));
  const raw = clamped.map(count => (count / total) * 100);
  const floors = raw.map(Math.floor);
  const remainder = 100 - floors.reduce((sum, value) => sum + value, 0);

  const byRemainingFraction = raw
    .map((value, index) => ({ index, fraction: value - floors[index] }))
    .sort((a, b) => b.fraction - a.fraction);

  const result = [...floors];
  // Bounded by array length: `remainder` only stays within [0, counts.length)
  // when `counts` fully accounts for `total`; otherwise there aren't enough
  // options to distribute every remainder point to, so it's spent partially.
  for (let i = 0; i < remainder && i < byRemainingFraction.length; i += 1) {
    result[byRemainingFraction[i].index] += 1;
  }
  return result;
};

/**
 * Whether an insight has any actual option/item data to chart. An insight
 * whose `kind` doesn't match either known case (a live payload diverging from
 * the declared union) isn't ours to silently drop here — it returns `true` so
 * it still reaches the renderer, which warns instead of vanishing it quietly.
 */
export const hasInsightData = (insight: Insight): boolean => {
  if (insight.kind === 'single_select') return insight.options.length > 0;
  if (insight.kind === 'frequency') return insight.items.length > 0;
  return true;
};
